import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Plus, Loader2, Users, Car, CalendarClock, FileText, AlertTriangle, Wallet, Pencil, Trash2, Power, Search, Phone, Mail, CreditCard, Building2 } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import DashboardLayout from "@/components/DashboardLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RFCField, PhoneField, PlateField, CurrencyField, DateField, TimeField } from "@/components/ui/forms";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { fmtDate } from "@/lib/datetime";

// Conversión entre valores del backend (date/time "de pared") y el ISO UTC que emiten DateField/TimeField
const dateToIso = (ymd) => (ymd ? new Date(`${ymd}T00:00:00`).toISOString() : "");
const isoToYmd = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const hmToIso = (hm) => {
  if (!hm) return "";
  const [h, m] = hm.split(":");
  const d = new Date();
  d.setHours(Number(h), Number(m), 0, 0);
  return d.toISOString();
};
const isoToHm = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const ymdToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const ymdMinusYears = (n) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));

// Catálogo oficial SAT (regímenes fiscales más usados)
const REGIMENES_SAT = [
  { code: "601", label: "General de Ley Personas Morales" },
  { code: "603", label: "Personas Morales con Fines no Lucrativos" },
  { code: "605", label: "Sueldos y Salarios e Ingresos Asimilados a Salarios" },
  { code: "606", label: "Arrendamiento" },
  { code: "612", label: "Personas Físicas con Actividades Empresariales y Profesionales" },
  { code: "621", label: "Incorporación Fiscal" },
  { code: "626", label: "Régimen Simplificado de Confianza (RESICO)" },
  { code: "616", label: "Sin obligaciones fiscales" },
];
const PREF_PAGO = ["Efectivo", "Tarjeta", "Transferencia", "Otro"];

const ESTATUS_STYLE = {
  Vigente: "text-[#047857] border-[#10B981]/40 bg-[#10B981]/5",
  Vencido: "text-[#B45309] border-[#F59E0B]/40 bg-[#F59E0B]/5",
  Suspendido: "text-[#B91C1C] border-[#EF4444]/40 bg-[#EF4444]/5",
  Cancelado: "text-[#52525B] border-[#A1A1AA]/40 bg-[#A1A1AA]/5",
};

const fld = "h-11 rounded-none border-[#0A0A0A]";
const lbl = "text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]";

export default function PensionesPostpago() {
  const [tab, setTab] = useState("contratos");
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [alertas, setAlertas] = useState([]);

  const [form, setForm] = useState(null); // {tipo, mode:'create'|'edit', id, data}
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // {tipo, id, label}
  const PLURAL = { cliente: "clientes", vehiculo: "vehiculos", plan: "planes", contrato: "contratos" };

  const [cuenta, setCuenta] = useState(null); // contrato seleccionado para estado de cuenta
  const [cargos, setCargos] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [cuentaLoading, setCuentaLoading] = useState(false);
  const [pagoForm, setPagoForm] = useState({ monto: "", metodo: "Efectivo", referencia: "" });
  const [savingPago, setSavingPago] = useState(false);
  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroEstatus, setFiltroEstatus] = useState("Todos");
  const [clienteBusqueda, setClienteBusqueda] = useState("");
  const [expediente, setExpediente] = useState(null); // { cliente, vehiculos, contratos }
  const [expLoading, setExpLoading] = useState(false);
  const [expedienteReabrir, setExpedienteReabrir] = useState(null); // id_cliente para reabrir tras cerrar el cobro

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, v, p, ct, su, al] = await Promise.all([
        api.get("/pension/clientes"), api.get("/pension/vehiculos"), api.get("/pension/planes"),
        api.get("/pension/contratos"), listSucursales(), api.get("/pension/alertas"),
      ]);
      setClientes(c.data); setVehiculos(v.data); setPlanes(p.data);
      setContratos(ct.data); setSucursales(su.data); setAlertas(al.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cliMap = useMemo(() => Object.fromEntries(clientes.map((c) => [c.id_cliente_pension, c])), [clientes]);
  const vehMap = useMemo(() => Object.fromEntries(vehiculos.map((v) => [v.id_vehiculo_pension, v])), [vehiculos]);
  const planMap = useMemo(() => Object.fromEntries(planes.map((p) => [p.id_plan_pension, p])), [planes]);
  const sucMap = useMemo(() => Object.fromEntries(sucursales.map((s) => [s.id_sucursal, s])), [sucursales]);

  const nombreCliente = (v) => (v && cliMap[v.id_cliente_pension]?.nombre) || "—";

  const contratosFiltrados = useMemo(() => {
    const t = filtroTexto.trim().toLowerCase();
    return contratos.filter((c) => {
      if (filtroEstatus !== "Todos" && c.estatus !== filtroEstatus) return false;
      if (!t) return true;
      const v = vehMap[c.id_vehiculo_pension];
      const cli = v ? (cliMap[v.id_cliente_pension]?.nombre || "") : "";
      return (v?.placas || "").toLowerCase().includes(t) || cli.toLowerCase().includes(t);
    });
  }, [contratos, filtroTexto, filtroEstatus, vehMap, cliMap]);

  const clientesFiltrados = useMemo(() => {
    const t = clienteBusqueda.trim().toLowerCase();
    if (!t) return clientes;
    return clientes.filter((c) =>
      (c.nombre || "").toLowerCase().includes(t) ||
      (c.telefono || "").toLowerCase().includes(t) ||
      (c.datos_fiscales?.rfc || "").toLowerCase().includes(t)
    );
  }, [clientes, clienteBusqueda]);

  const abrirExpediente = async (id) => {
    setExpediente({ loading: true });
    setExpLoading(true);
    try {
      const { data } = await api.get(`/pension/clientes/${id}/expediente`);
      setExpediente(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setExpediente(null);
    } finally { setExpLoading(false); }
  };

  // Acceso rápido desde el Expediente: abre el panel de cobro del contrato sin perder el contexto del cliente.
  const abrirCobroDesdeExpediente = (c) => {
    setExpedienteReabrir(expediente?.cliente?.id_cliente_pension || null);
    setExpediente(null);
    abrirCuenta({
      id_contrato_pension: c.id_contrato_pension,
      id_vehiculo_pension: c.id_vehiculo_pension,
      saldo_pendiente: c.saldo_pendiente,
      limite_credito: c.limite_credito,
      estatus: c.estatus,
    });
  };

  // ---------- Formularios CRUD ----------
  const openForm = (tipo) => {
    const defaults = {
      cliente: { nombre: "", telefono: "", correo: "", preferencia_pago: "", rfc: "", codigo_postal: "", regimen_fiscal: "" },
      vehiculo: { id_cliente_pension: "", placas: "", marca: "", modelo: "", color: "", qr_tag: "" },
      plan: { nombre: "", tipo: "", hora_inicio: "", hora_fin: "" },
      contrato: { id_vehiculo_pension: "", id_plan_pension: "", id_sucursal: "", tarifa_pactada: "", fecha_inicio: "", dia_corte: "1", limite_credito: "", estatus: "Vigente" },
    };
    setForm({ tipo, mode: "create", id: null, data: defaults[tipo] });
  };

  const openEdit = (tipo, rec) => {
    let data;
    if (tipo === "cliente") {
      const f = rec.datos_fiscales || {};
      data = { nombre: rec.nombre || "", telefono: rec.telefono || "", correo: rec.correo || "", preferencia_pago: rec.preferencia_pago || "",
        rfc: f.rfc || "", codigo_postal: f.codigo_postal || "", regimen_fiscal: f.regimen_fiscal || "" };
    } else if (tipo === "vehiculo") {
      data = { id_cliente_pension: rec.id_cliente_pension, placas: rec.placas || "", marca: rec.marca || "", modelo: rec.modelo || "", color: rec.color || "", qr_tag: rec.qr_tag || "" };
    } else if (tipo === "plan") {
      data = { nombre: rec.nombre || "", tipo: rec.tipo || "", hora_inicio: rec.hora_inicio ? hmToIso(String(rec.hora_inicio).slice(0,5)) : "", hora_fin: rec.hora_fin ? hmToIso(String(rec.hora_fin).slice(0,5)) : "" };
    } else {
      data = { id_vehiculo_pension: rec.id_vehiculo_pension, id_plan_pension: rec.id_plan_pension, id_sucursal: rec.id_sucursal,
        tarifa_pactada: String(rec.tarifa_pactada), fecha_inicio: dateToIso(rec.fecha_inicio), dia_corte: String(rec.dia_corte), limite_credito: String(rec.limite_credito), estatus: rec.estatus };
    }
    const idKey = { cliente: "id_cliente_pension", vehiculo: "id_vehiculo_pension", plan: "id_plan_pension", contrato: "id_contrato_pension" }[tipo];
    setForm({ tipo, mode: "edit", id: rec[idKey], data });
  };
  const setF = (k, v) => setForm((f) => ({ ...f, data: { ...f.data, [k]: v } }));

  const buildFiscal = (d) => {
    const f = {};
    if (d.rfc) f.rfc = d.rfc.trim().toUpperCase();
    if (d.regimen_fiscal) f.regimen_fiscal = d.regimen_fiscal.trim();
    if (d.codigo_postal) f.codigo_postal = d.codigo_postal.trim();
    if (d.rfc && d.nombre) f.razon_social = d.nombre.trim(); // CFDI reutiliza el nombre como razón social
    return Object.keys(f).length ? f : null;
  };

  const saveForm = async () => {
    const { tipo, mode, id, data } = form;
    setSaving(true);
    try {
      let payload;
      if (tipo === "cliente") {
        if (!data.nombre.trim()) throw { response: { data: { detail: "El nombre es obligatorio" } } };
        if (data.rfc && !data.regimen_fiscal) throw { response: { data: { detail: "El régimen fiscal es obligatorio cuando capturas RFC" } } };
        payload = { nombre: data.nombre.trim(), telefono: data.telefono || null, correo: data.correo || null, preferencia_pago: data.preferencia_pago || null, datos_fiscales: buildFiscal(data) };
      } else if (tipo === "vehiculo") {
        if (!data.id_cliente_pension) throw { response: { data: { detail: "Selecciona un cliente" } } };
        if (!data.placas.trim()) throw { response: { data: { detail: "Las placas son obligatorias" } } };
        payload = { id_cliente_pension: data.id_cliente_pension, placas: data.placas.trim(), marca: data.marca || null, modelo: data.modelo || null, color: data.color || null, qr_tag: data.qr_tag.trim() || null };
      } else if (tipo === "plan") {
        if (!data.nombre.trim()) throw { response: { data: { detail: "El nombre del plan es obligatorio" } } };
        payload = { nombre: data.nombre.trim(), tipo: data.tipo || null, hora_inicio: isoToHm(data.hora_inicio), hora_fin: isoToHm(data.hora_fin) };
      } else {
        if (!data.id_vehiculo_pension || !data.id_plan_pension || !data.id_sucursal) throw { response: { data: { detail: "Vehículo, plan y sucursal son obligatorios" } } };
        if (!data.fecha_inicio) throw { response: { data: { detail: "La fecha de inicio es obligatoria" } } };
        const fi = isoToYmd(data.fecha_inicio);
        if (fi > ymdToday()) throw { response: { data: { detail: "La fecha de inicio no puede ser futura" } } };
        if (fi < ymdMinusYears(1)) throw { response: { data: { detail: "La fecha de inicio no puede tener más de 1 año de antigüedad" } } };
        payload = { id_vehiculo_pension: data.id_vehiculo_pension, id_plan_pension: data.id_plan_pension, id_sucursal: data.id_sucursal,
          tarifa_pactada: Number(Number(data.tarifa_pactada || 0).toFixed(2)), fecha_inicio: isoToYmd(data.fecha_inicio), dia_corte: parseInt(data.dia_corte || 1, 10),
          limite_credito: Number(Number(data.limite_credito || 0).toFixed(2)) };
        if (mode === "edit") payload.estatus = data.estatus;
      }
      if (mode === "edit") await api.put(`/pension/${PLURAL[tipo]}/${id}`, payload);
      else await api.post(`/pension/${PLURAL[tipo]}`, payload);
      toast.success(mode === "edit" ? "Actualizado correctamente" : "Guardado correctamente");
      setForm(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    const c = confirm; setConfirm(null);
    if (!c) return;
    try {
      await api.delete(`/pension/${PLURAL[c.tipo]}/${c.id}`);
      toast.success("Registro desactivado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // ---------- Estado de cuenta ----------
  const abrirCuenta = async (contrato) => {
    setCuenta(contrato); setCargos([]); setPagos([]); setPagoForm({ monto: "", metodo: "Efectivo", referencia: "" });
    setCuentaLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get(`/pension/cargos?id_contrato_pension=${contrato.id_contrato_pension}`),
        api.get(`/pension/pagos?id_contrato_pension=${contrato.id_contrato_pension}`),
      ]);
      setCargos(c.data); setPagos(p.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCuentaLoading(false); }
  };

  const registrarPago = async () => {
    const monto = Number(pagoForm.monto);
    if (!monto || monto <= 0) return toast.error("Captura un monto válido");
    setSavingPago(true);
    try {
      await api.post("/pension/pagos", {
        id_contrato_pension: cuenta.id_contrato_pension, monto: Number(monto.toFixed(2)),
        metodo: pagoForm.metodo || null, referencia: pagoForm.referencia || null,
      });
      toast.success("Pago registrado");
      const [ct, p, c, al] = await Promise.all([
        api.get("/pension/contratos"), api.get(`/pension/pagos?id_contrato_pension=${cuenta.id_contrato_pension}`),
        api.get(`/pension/cargos?id_contrato_pension=${cuenta.id_contrato_pension}`), api.get("/pension/alertas"),
      ]);
      setContratos(ct.data); setPagos(p.data); setCargos(c.data); setAlertas(al.data);
      const actualizado = ct.data.find((x) => x.id_contrato_pension === cuenta.id_contrato_pension);
      if (actualizado) setCuenta(actualizado);
      setPagoForm({ monto: "", metodo: "Efectivo", referencia: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPago(false); }
  };

  const pct = (saldo, limite) => (Number(limite) > 0 ? Math.min(100, (Number(saldo) / Number(limite)) * 100) : 0);
  const barColor = (p) => (p >= 80 ? "bg-[#EF4444]" : p >= 50 ? "bg-[#F59E0B]" : "bg-[#10B981]");

  const nuevoBtn = (tipo, label) => (
    <button onClick={() => openForm(tipo)} data-testid={`new-${tipo}-btn`}
      className="btn-hard h-10 px-4 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-xs flex items-center gap-2">
      <Plus className="h-4 w-4" /> {label}
    </button>
  );

  const th = "font-display font-bold px-4 py-3 text-xs tracking-wider uppercase text-left";

  const Acciones = ({ tipo, rec, label }) => (
    <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => openEdit(tipo, rec)} data-testid={`edit-${tipo}-btn`} className="h-8 w-8 inline-flex items-center justify-center border border-[#e4e4e7] hover:border-[#0A0A0A] transition-colors duration-200"><Pencil className="h-3.5 w-3.5" /></button>
      <button onClick={() => setConfirm({ tipo, id: rec[{ cliente: "id_cliente_pension", vehiculo: "id_vehiculo_pension", plan: "id_plan_pension", contrato: "id_contrato_pension" }[tipo]], label })} data-testid={`del-${tipo}-btn`} className="h-8 w-8 inline-flex items-center justify-center border border-[#e4e4e7] text-[#B45309] hover:border-[#F59E0B] transition-colors duration-200"><Power className="h-3.5 w-3.5" /></button>
    </div>
  );

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2">Módulo de pensionados</p>
          <h1 className="font-display text-4xl font-black tracking-tight">Pensionados</h1>
        </div>

        {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-none bg-transparent border-b border-[#e4e4e7] w-full justify-start h-auto p-0 gap-1" data-testid="pension-tabs">
              {[
                ["contratos", "Contratos", FileText],
                ["clientes", "Clientes", Users],
                ["vehiculos", "Vehículos", Car],
                ["planes", "Planes", CalendarClock],
                ["alertas", "Alertas", AlertTriangle],
              ].map(([v, label, Icon]) => (
                <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                  className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                  <Icon className="h-4 w-4" /> {label}
                  {v === "alertas" && alertas.length > 0 && <span className="ml-1 bg-[#EF4444] text-white text-[10px] px-1.5 rounded-full">{alertas.length}</span>}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* CONTRATOS */}
            <TabsContent value="contratos" className="mt-6">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-4">
                <div className="flex gap-2 flex-1">
                  <Input value={filtroTexto} onChange={(e) => setFiltroTexto(e.target.value)} placeholder="Buscar por cliente o placa…"
                    data-testid="contrato-buscar" className="h-10 rounded-none border-[#0A0A0A] max-w-xs" />
                  <Select value={filtroEstatus} onValueChange={setFiltroEstatus}>
                    <SelectTrigger data-testid="contrato-filtro-estatus" className="h-10 rounded-none border-[#0A0A0A] w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Todos">Todos</SelectItem>
                      <SelectItem value="Vigente">Vigente</SelectItem>
                      <SelectItem value="Suspendido">Suspendido</SelectItem>
                      <SelectItem value="Vencido">Vencido</SelectItem>
                      <SelectItem value="Cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {nuevoBtn("contrato", "Nuevo contrato")}
              </div>
              <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm" data-testid="contratos-table">
                  <thead><tr className="border-b border-[#e4e4e7]">
                    <th className={th}>Cliente</th><th className={th}>Placa</th><th className={th}>Plan</th>
                    <th className={th}>Estatus</th><th className={th}>Saldo / Límite</th><th className={`${th} text-right`}>Acción</th>
                  </tr></thead>
                  <tbody>
                    {contratosFiltrados.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-12 text-center text-[#52525B]">{contratos.length === 0 ? "Sin contratos registrados." : "Sin resultados para el filtro."}</td></tr>
                    ) : contratosFiltrados.map((c) => {
                      const v = vehMap[c.id_vehiculo_pension];
                      const p = pct(c.saldo_pendiente, c.limite_credito);
                      return (
                        <tr key={c.id_contrato_pension} className="border-b border-[#f0f0f1] hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                          onClick={() => abrirCuenta(c)} data-testid={`contrato-row-${c.id_contrato_pension}`}>
                          <td className="px-4 py-3 font-semibold">{nombreCliente(v)}</td>
                          <td className="px-4 py-3 font-mono">{v?.placas || "—"}</td>
                          <td className="px-4 py-3">{planMap[c.id_plan_pension]?.nombre || "—"}</td>
                          <td className="px-4 py-3"><span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${ESTATUS_STYLE[c.estatus] || ""}`}>{c.estatus}</span></td>
                          <td className="px-4 py-3 min-w-[180px]">
                            <div className="flex justify-between text-xs mb-1"><span className="font-mono font-bold">{fmt(c.saldo_pendiente)}</span><span className="text-[#52525B] font-mono">{fmt(c.limite_credito)}</span></div>
                            <div className="h-1.5 w-full bg-[#e4e4e7] overflow-hidden"><div className={`h-full ${barColor(p)} transition-all duration-300`} style={{ width: `${p}%` }} data-testid={`saldo-bar-${c.id_contrato_pension}`} /></div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#0A0A0A]"><Wallet className="h-3.5 w-3.5" /> Cuenta</span>
                              <Acciones tipo="contrato" rec={c} label={`contrato de ${nombreCliente(v)}`} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* CLIENTES */}
            <TabsContent value="clientes" className="mt-6">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
                <div className="relative w-full sm:max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#52525B]" />
                  <Input value={clienteBusqueda} onChange={(e) => setClienteBusqueda(e.target.value)} placeholder="Buscar por nombre, teléfono o RFC…"
                    data-testid="clientes-search-input" className="h-10 pl-9 rounded-none border-[#0A0A0A]" />
                </div>
                {nuevoBtn("cliente", "Nuevo cliente")}
              </div>
              <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm" data-testid="clientes-table">
                  <thead><tr className="border-b border-[#e4e4e7]"><th className={th}>Nombre</th><th className={th}>Teléfono</th><th className={th}>Correo</th><th className={th}>RFC</th><th className={`${th} text-right`}>Acciones</th></tr></thead>
                  <tbody>
                    {clientesFiltrados.length === 0 ? <tr><td colSpan={5} className="px-4 py-12 text-center text-[#52525B]" data-testid="clientes-empty">{clientes.length === 0 ? "Sin clientes." : "Sin resultados para la búsqueda."}</td></tr>
                    : clientesFiltrados.map((c) => (
                      <tr key={c.id_cliente_pension} className="border-b border-[#f0f0f1]" data-testid={`cliente-row-${c.id_cliente_pension}`}>
                        <td className="px-4 py-3">
                          <button onClick={() => abrirExpediente(c.id_cliente_pension)} data-testid={`cliente-open-${c.id_cliente_pension}`}
                            className="font-semibold text-left hover:text-[#B45309] hover:underline underline-offset-4 transition-colors duration-200">{c.nombre}</button>
                        </td>
                        <td className="px-4 py-3">{c.telefono || "—"}</td>
                        <td className="px-4 py-3">{c.correo || "—"}</td><td className="px-4 py-3 font-mono">{c.datos_fiscales?.rfc || "—"}</td>
                        <td className="px-4 py-3"><Acciones tipo="cliente" rec={c} label={c.nombre} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* VEHICULOS */}
            <TabsContent value="vehiculos" className="mt-6">
              <div className="flex justify-end mb-4">{nuevoBtn("vehiculo", "Nuevo vehículo")}</div>
              <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm" data-testid="vehiculos-table">
                  <thead><tr className="border-b border-[#e4e4e7]"><th className={th}>Placa</th><th className={th}>Cliente</th><th className={th}>Marca / Modelo</th><th className={th}>Color</th><th className={th}>QR / TAG</th><th className={`${th} text-right`}>Acciones</th></tr></thead>
                  <tbody>
                    {vehiculos.length === 0 ? <tr><td colSpan={6} className="px-4 py-12 text-center text-[#52525B]">Sin vehículos.</td></tr>
                    : vehiculos.map((v) => (
                      <tr key={v.id_vehiculo_pension} className="border-b border-[#f0f0f1]" data-testid={`vehiculo-row-${v.id_vehiculo_pension}`}>
                        <td className="px-4 py-3 font-mono font-semibold">{v.placas}</td><td className="px-4 py-3">{nombreCliente(v)}</td>
                        <td className="px-4 py-3">{[v.marca, v.modelo].filter(Boolean).join(" ") || "—"}</td>
                        <td className="px-4 py-3">{v.color || "—"}</td><td className="px-4 py-3 font-mono">{v.qr_tag || "—"}</td>
                        <td className="px-4 py-3"><Acciones tipo="vehiculo" rec={v} label={v.placas} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* PLANES */}
            <TabsContent value="planes" className="mt-6">
              <div className="flex justify-end mb-4">{nuevoBtn("plan", "Nuevo plan")}</div>
              <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm" data-testid="planes-table">
                  <thead><tr className="border-b border-[#e4e4e7]"><th className={th}>Nombre</th><th className={th}>Tipo</th><th className={th}>Horario</th><th className={`${th} text-right`}>Acciones</th></tr></thead>
                  <tbody>
                    {planes.length === 0 ? <tr><td colSpan={4} className="px-4 py-12 text-center text-[#52525B]">Sin planes.</td></tr>
                    : planes.map((p) => (
                      <tr key={p.id_plan_pension} className="border-b border-[#f0f0f1]" data-testid={`plan-row-${p.id_plan_pension}`}>
                        <td className="px-4 py-3 font-semibold">{p.nombre}</td><td className="px-4 py-3">{p.tipo || "—"}</td>
                        <td className="px-4 py-3 font-mono">{p.hora_inicio && p.hora_fin ? `${String(p.hora_inicio).slice(0,5)} – ${String(p.hora_fin).slice(0,5)}` : "24 h"}</td>
                        <td className="px-4 py-3"><Acciones tipo="plan" rec={p} label={p.nombre} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* ALERTAS */}
            <TabsContent value="alertas" className="mt-6">
              <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm" data-testid="alertas-table">
                  <thead><tr className="border-b border-[#e4e4e7]"><th className={th}>Cliente</th><th className={th}>Placa</th><th className={th}>Uso del crédito</th><th className={th}>Saldo / Límite</th><th className={th}>Estatus</th></tr></thead>
                  <tbody>
                    {alertas.length === 0 ? <tr><td colSpan={5} className="px-4 py-12 text-center text-[#52525B]">Sin alertas de crédito. Todos los contratos están por debajo del 80%.</td></tr>
                    : alertas.map((a) => (
                      <tr key={a.id_contrato_pension} className="border-b border-[#f0f0f1]" data-testid={`alerta-row-${a.id_contrato_pension}`}>
                        <td className="px-4 py-3 font-semibold">{a.cliente}</td><td className="px-4 py-3 font-mono">{a.placas}</td>
                        <td className="px-4 py-3"><span className="font-display font-black text-[#B91C1C]">{a.porcentaje_uso}%</span></td>
                        <td className="px-4 py-3 font-mono">{fmt(a.saldo_pendiente)} / {fmt(a.limite_credito)}</td>
                        <td className="px-4 py-3"><span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${ESTATUS_STYLE[a.estatus] || ""}`}>{a.estatus}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </motion.div>

      {/* ---------- Modal CRUD ---------- */}
      <Dialog open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }}>
        <DialogContent className="rounded-none border-[#0A0A0A] max-w-lg" data-testid="pension-form-dialog">
          {form && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-black tracking-tight capitalize">{form.mode === "edit" ? "Editar" : "Nuevo"} {form.tipo}</DialogTitle>
                <DialogDescription>Captura los datos y guarda para registrar en el sistema.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {form.tipo === "cliente" && (
                  <>
                    <p className={`${lbl} mb-1`}>Datos generales</p>
                    <Field l="Nombre / Razón social"><Input value={form.data.nombre} onChange={(e) => setF("nombre", e.target.value)} data-testid="f-cliente-nombre" className={fld} /></Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field l="Teléfono"><PhoneField value={form.data.telefono} onChange={(v) => setF("telefono", v)} data-testid="f-cliente-tel" className={fld} /></Field>
                      <Field l="Preferencia de pago">
                        <Select value={form.data.preferencia_pago} onValueChange={(v) => setF("preferencia_pago", v)}>
                          <SelectTrigger data-testid="f-cliente-pref" className={fld}><SelectValue placeholder="Sugerencia…" /></SelectTrigger>
                          <SelectContent>{PREF_PAGO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <Field l="Correo"><Input type="email" value={form.data.correo} onChange={(e) => setF("correo", e.target.value)} data-testid="f-cliente-correo" className={fld} /></Field>
                    <div className="border-t border-[#e4e4e7] pt-3 mt-1">
                      <p className={`${lbl} mb-3`}>Datos fiscales (opcional)</p>
                      <div className="grid grid-cols-2 gap-3">
                        <Field l="RFC"><RFCField value={form.data.rfc} onChange={(v) => setF("rfc", v)} data-testid="f-cliente-rfc" className={fld} /></Field>
                        <Field l="Código postal"><Input value={form.data.codigo_postal} onChange={(e) => setF("codigo_postal", e.target.value.replace(/\D/g, "").slice(0, 5))} inputMode="numeric" data-testid="f-cliente-cp" className={fld} /></Field>
                        <div className="col-span-2">
                          <Field l="Régimen fiscal">
                            <Select value={form.data.regimen_fiscal} onValueChange={(v) => setF("regimen_fiscal", v)}>
                              <SelectTrigger data-testid="f-cliente-regimen" className={fld}><SelectValue placeholder="Selecciona régimen…" /></SelectTrigger>
                              <SelectContent>{REGIMENES_SAT.map((r) => <SelectItem key={r.code} value={r.code} data-testid={`regimen-opt-${r.code}`}>{r.code} · {r.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </Field>
                        </div>
                      </div>
                      <p className="text-[11px] text-[#52525B] mt-2">Si capturas RFC, el régimen fiscal es obligatorio (requerido para CFDI).</p>
                    </div>
                  </>
                )}
                {form.tipo === "vehiculo" && (
                  <>
                    <Field l="Cliente">
                      <Select value={form.data.id_cliente_pension} onValueChange={(v) => setF("id_cliente_pension", v)}>
                        <SelectTrigger data-testid="f-veh-cliente" className={fld}><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                        <SelectContent>{clientes.map((c) => <SelectItem key={c.id_cliente_pension} value={c.id_cliente_pension}>{c.nombre}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field l="Placas"><PlateField value={form.data.placas} onChange={(v) => setF("placas", v)} data-testid="f-veh-placas" className={fld} /></Field>
                      <Field l="QR / TAG (opcional)"><Input value={form.data.qr_tag} onChange={(e) => setF("qr_tag", e.target.value)} data-testid="f-veh-tag" className={fld} /></Field>
                      <Field l="Marca"><Input value={form.data.marca} onChange={(e) => setF("marca", e.target.value)} data-testid="f-veh-marca" className={fld} /></Field>
                      <Field l="Modelo"><Input value={form.data.modelo} onChange={(e) => setF("modelo", e.target.value)} data-testid="f-veh-modelo" className={fld} /></Field>
                      <Field l="Color"><Input value={form.data.color} onChange={(e) => setF("color", e.target.value)} data-testid="f-veh-color" className={fld} /></Field>
                    </div>
                  </>
                )}
                {form.tipo === "plan" && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <Field l="Nombre"><Input value={form.data.nombre} onChange={(e) => setF("nombre", e.target.value)} data-testid="f-plan-nombre" className={fld} /></Field>
                      <Field l="Tipo"><Input value={form.data.tipo} onChange={(e) => setF("tipo", e.target.value)} placeholder="Nocturno, Diurno…" data-testid="f-plan-tipo" className={fld} /></Field>
                      <Field l="Hora inicio"><TimeField value={form.data.hora_inicio} onChange={(v) => setF("hora_inicio", v)} data-testid="f-plan-inicio" className={fld} /></Field>
                      <Field l="Hora fin"><TimeField value={form.data.hora_fin} onChange={(v) => setF("hora_fin", v)} data-testid="f-plan-fin" className={fld} /></Field>
                    </div>
                    <p className="text-[11px] text-[#52525B]">Deja las horas vacías para un plan de 24 h (sin excedente por horario).</p>
                  </>
                )}
                {form.tipo === "contrato" && (
                  <>
                    <Field l="Vehículo">
                      <Select value={form.data.id_vehiculo_pension} onValueChange={(v) => setF("id_vehiculo_pension", v)}>
                        <SelectTrigger data-testid="f-ct-vehiculo" className={fld}><SelectValue placeholder="Selecciona vehículo" /></SelectTrigger>
                        <SelectContent>{vehiculos.map((v) => <SelectItem key={v.id_vehiculo_pension} value={v.id_vehiculo_pension}>{v.placas} · {nombreCliente(v)}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field l="Plan">
                        <Select value={form.data.id_plan_pension} onValueChange={(v) => setF("id_plan_pension", v)}>
                          <SelectTrigger data-testid="f-ct-plan" className={fld}><SelectValue placeholder="Plan" /></SelectTrigger>
                          <SelectContent>{planes.map((p) => <SelectItem key={p.id_plan_pension} value={p.id_plan_pension}>{p.nombre}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field l="Sucursal">
                        <Select value={form.data.id_sucursal} onValueChange={(v) => setF("id_sucursal", v)}>
                          <SelectTrigger data-testid="f-ct-sucursal" className={fld}><SelectValue placeholder="Sucursal" /></SelectTrigger>
                          <SelectContent>{sucursales.map((s) => <SelectItem key={s.id_sucursal} value={s.id_sucursal}>{s.nombre}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field l="Tarifa pactada"><CurrencyField value={form.data.tarifa_pactada} onChange={(v) => setF("tarifa_pactada", v)} data-testid="f-ct-tarifa" className={fld} /></Field>
                      <Field l="Límite de crédito"><CurrencyField value={form.data.limite_credito} onChange={(v) => setF("limite_credito", v)} data-testid="f-ct-limite" className={fld} /></Field>
                      <Field l="Fecha de inicio"><DateField value={form.data.fecha_inicio} onChange={(v) => setF("fecha_inicio", v)} showToday min={ymdMinusYears(1)} max={ymdToday()} data-testid="f-ct-fecha" className={`${fld} w-full`} /></Field>
                      <Field l="Día de corte"><Input type="number" min="1" max="31" value={form.data.dia_corte} onChange={(e) => setF("dia_corte", e.target.value)} data-testid="f-ct-corte" className={fld} /></Field>
                    </div>
                    {form.mode === "edit" && (
                      <Field l="Estatus del contrato">
                        <Select value={form.data.estatus} onValueChange={(v) => setF("estatus", v)}>
                          <SelectTrigger data-testid="f-ct-estatus" className={fld}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Vigente">Vigente</SelectItem>
                            <SelectItem value="Suspendido">Suspendido</SelectItem>
                            <SelectItem value="Vencido">Vencido</SelectItem>
                            <SelectItem value="Cancelado">Cancelado</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                    )}
                  </>
                )}
                <button onClick={saveForm} disabled={saving} data-testid="pension-form-save"
                  className="w-full h-12 bg-[#0A0A0A] text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#27272A] transition-colors duration-200 disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Drawer Estado de cuenta ---------- */}
      <Sheet open={!!cuenta} onOpenChange={(o) => { if (!o) { setCuenta(null); if (expedienteReabrir) { const id = expedienteReabrir; setExpedienteReabrir(null); abrirExpediente(id); } } }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto border-l-[#0A0A0A]" data-testid="cuenta-drawer">
          {cuenta && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl font-black tracking-tight">Estado de cuenta</SheetTitle>
                <SheetDescription>{vehMap[cuenta.id_vehiculo_pension]?.placas} · {nombreCliente(vehMap[cuenta.id_vehiculo_pension])}</SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-6">
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-[#e4e4e7] p-4"><p className={lbl}>Saldo pendiente</p><p className="font-display text-2xl font-black mt-1" data-testid="cuenta-saldo">{fmt(cuenta.saldo_pendiente)}</p></div>
                  <div className="border border-[#e4e4e7] p-4"><p className={lbl}>Límite de crédito</p><p className="font-display text-2xl font-black mt-1">{fmt(cuenta.limite_credito)}</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${ESTATUS_STYLE[cuenta.estatus] || ""}`} data-testid="cuenta-estatus">{cuenta.estatus}</span>
                  <span className="text-xs text-[#52525B]">{pct(cuenta.saldo_pendiente, cuenta.limite_credito).toFixed(0)}% del crédito usado</span>
                </div>

                {/* Registrar pago */}
                <div className="border border-[#0A0A0A] p-4 space-y-3">
                  <Label className={`${lbl} block`}>Registrar pago (abono)</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <CurrencyField placeholder="Monto" value={pagoForm.monto} onChange={(v) => setPagoForm({ ...pagoForm, monto: v })} data-testid="pago-monto" className={fld} />
                    <Select value={pagoForm.metodo} onValueChange={(v) => setPagoForm({ ...pagoForm, metodo: v })}>
                      <SelectTrigger data-testid="pago-metodo" className={fld}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Efectivo">Efectivo</SelectItem>
                        <SelectItem value="Transferencia">Transferencia</SelectItem>
                        <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="Referencia" value={pagoForm.referencia} onChange={(e) => setPagoForm({ ...pagoForm, referencia: e.target.value })} data-testid="pago-referencia" className={fld} />
                  </div>
                  <button onClick={registrarPago} disabled={savingPago} data-testid="registrar-pago-btn"
                    className="w-full h-11 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                    {savingPago ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wallet className="h-4 w-4" /> Registrar pago</>}
                  </button>
                </div>

                {cuentaLoading ? <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
                  <>
                    <div>
                      <h4 className="font-display font-bold text-sm uppercase tracking-wider mb-2">Cargos</h4>
                      <div className="border border-[#e4e4e7] divide-y divide-[#f0f0f1]" data-testid="cuenta-cargos">
                        {cargos.length === 0 ? <p className="p-4 text-center text-xs text-[#52525B]">Sin cargos.</p>
                        : cargos.map((c) => (
                          <div key={c.id_cargo_pension} className="flex justify-between items-center px-3 py-2.5 text-sm">
                            <div><p className="font-medium">{c.concepto}</p><span className={`text-[10px] uppercase font-bold ${c.estatus_pago === "Pagado" ? "text-[#047857]" : "text-[#B45309]"}`}>{c.estatus_pago}</span></div>
                            <span className="font-mono font-bold">{fmt(c.monto)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-sm uppercase tracking-wider mb-2">Pagos</h4>
                      <div className="border border-[#e4e4e7] divide-y divide-[#f0f0f1]" data-testid="cuenta-pagos">
                        {pagos.length === 0 ? <p className="p-4 text-center text-xs text-[#52525B]">Sin pagos.</p>
                        : pagos.map((p) => (
                          <div key={p.id_pago_pension} className="flex justify-between items-center px-3 py-2.5 text-sm">
                            <div><p className="font-medium">{p.metodo || "Pago"}</p><span className="text-[10px] text-[#52525B]">{p.referencia || ""} · {fmtDate(p.fecha_pago)}</span></div>
                            <span className="font-mono font-bold text-[#047857]">{fmt(p.monto)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ---------- Drawer Expediente 360° del Cliente ---------- */}
      <Sheet open={!!expediente} onOpenChange={(o) => { if (!o) setExpediente(null); }}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto border-l-[#0A0A0A]" data-testid="expediente-drawer">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl font-black tracking-tight">Expediente del Cliente</SheetTitle>
            <SheetDescription>{expediente?.cliente?.nombre || "Cargando…"}</SheetDescription>
          </SheetHeader>
          {expLoading || !expediente?.cliente ? (
            <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <div className="mt-6 space-y-8">
              {/* Resumen */}
              <section data-testid="expediente-resumen">
                <h4 className="font-display font-bold text-sm uppercase tracking-wider mb-3">Resumen</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-[#e4e4e7] p-3"><p className={lbl}><Phone className="inline h-3 w-3 mr-1" />Teléfono</p><p className="font-medium mt-1" data-testid="exp-telefono">{expediente.cliente.telefono || "—"}</p></div>
                  <div className="border border-[#e4e4e7] p-3"><p className={lbl}><Mail className="inline h-3 w-3 mr-1" />Correo</p><p className="font-medium mt-1 truncate" data-testid="exp-correo">{expediente.cliente.correo || "—"}</p></div>
                  <div className="border border-[#e4e4e7] p-3">
                    <p className={lbl}><CreditCard className="inline h-3 w-3 mr-1" />Preferencia de pago</p>
                    <div className="mt-1">
                      {expediente.cliente.preferencia_pago
                        ? <span className="inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border text-[#1D4ED8] border-[#3B82F6]/40 bg-[#3B82F6]/5" data-testid="exp-preferencia">{expediente.cliente.preferencia_pago}</span>
                        : <span className="text-[#52525B]">—</span>}
                    </div>
                  </div>
                  <div className="border border-[#e4e4e7] p-3"><p className={lbl}><Building2 className="inline h-3 w-3 mr-1" />Régimen fiscal</p><p className="font-medium mt-1" data-testid="exp-regimen">{expediente.cliente.datos_fiscales?.regimen_fiscal ? `${expediente.cliente.datos_fiscales.regimen_fiscal}${(REGIMENES_SAT.find((r) => r.code === expediente.cliente.datos_fiscales.regimen_fiscal)?.label ? " · " + REGIMENES_SAT.find((r) => r.code === expediente.cliente.datos_fiscales.regimen_fiscal).label : "")}` : "—"}</p></div>
                </div>
                {expediente.cliente.datos_fiscales?.rfc && <p className="text-xs text-[#52525B] mt-2 font-mono">RFC: {expediente.cliente.datos_fiscales.rfc}</p>}
              </section>

              {/* Vehículos */}
              <section>
                <h4 className="font-display font-bold text-sm uppercase tracking-wider mb-2">Vehículos ({expediente.vehiculos.length})</h4>
                <div className="border border-[#e4e4e7] overflow-x-auto">
                  <table className="w-full text-sm" data-testid="expediente-vehiculos">
                    <thead><tr className="border-b border-[#e4e4e7]"><th className={th}>Placa</th><th className={th}>Marca</th><th className={th}>Modelo</th></tr></thead>
                    <tbody>
                      {expediente.vehiculos.length === 0 ? <tr><td colSpan={3} className="px-4 py-6 text-center text-xs text-[#52525B]">Sin vehículos vinculados.</td></tr>
                      : expediente.vehiculos.map((v) => (
                        <tr key={v.id_vehiculo_pension} className="border-b border-[#f0f0f1]">
                          <td className="px-4 py-2.5 font-mono font-semibold">{v.placas}</td>
                          <td className="px-4 py-2.5">{v.marca || "—"}</td>
                          <td className="px-4 py-2.5">{v.modelo || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Contratos */}
              <section>
                <h4 className="font-display font-bold text-sm uppercase tracking-wider mb-2">Contratos ({expediente.contratos.length})</h4>
                <div className="border border-[#e4e4e7] overflow-x-auto">
                  <table className="w-full text-sm" data-testid="expediente-contratos">
                    <thead><tr className="border-b border-[#e4e4e7]"><th className={th}>Sucursal</th><th className={th}>Plan</th><th className={th}>Estatus</th><th className={`${th} text-right`}>Saldo</th><th className={`${th} text-right`}>Acción</th></tr></thead>
                    <tbody>
                      {expediente.contratos.length === 0 ? <tr><td colSpan={5} className="px-4 py-6 text-center text-xs text-[#52525B]">Sin contratos.</td></tr>
                      : expediente.contratos.map((c) => (
                        <tr key={c.id_contrato_pension} className="border-b border-[#f0f0f1]">
                          <td className="px-4 py-2.5">{c.sucursal_nombre}</td>
                          <td className="px-4 py-2.5">{c.plan_nombre}</td>
                          <td className="px-4 py-2.5"><span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${ESTATUS_STYLE[c.estatus] || ""}`}>{c.estatus}</span></td>
                          <td className="px-4 py-2.5 text-right font-mono font-bold">{fmt(c.saldo_pendiente)}</td>
                          <td className="px-4 py-2.5 text-right">
                            <button onClick={() => abrirCobroDesdeExpediente(c)} data-testid={`exp-cobro-${c.id_contrato_pension}`}
                              className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-[10px] hover:bg-[#0A0A0A] hover:text-[#FFCC00] transition-colors duration-200">
                              <Wallet className="h-3.5 w-3.5" /> Estado de cuenta
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title="¿Desactivar registro?"
        description={confirm ? `Se desactivará "${confirm.label}" (baja lógica). Podrás reactivarlo desde el backend si es necesario.` : ""}
        confirmLabel="Desactivar"
        danger
        onConfirm={doDelete}
      />
    </DashboardLayout>
  );
}

function Field({ l, children }) {
  return (<div className="space-y-2"><Label className={lbl}>{l}</Label>{children}</div>);
}
